import React, { useState, useMemo } from 'react';
import { useClients, PIPELINE_STAGES } from '../context/ClientsContext';
import { useUsers } from '../context/UsersContext';
import { useConfig } from '../context/ConfigContext';
import { C, S } from '../utils/styles';
import Icon from './Icon';
import { fmtK, fmt } from '../utils/formatters';
import ClientDetailDrawer from './ClientDetailDrawer';
import SaveEmployerDialog from './SaveEmployerDialog';
import StyledSelect from './StyledSelect';
import useUnreadMessages from '../hooks/useUnreadMessages';
import { optimizeEmployeeWithEligibility } from '../utils/calculations';

export default function ClientsView({ onOpenAnalysis, autoOpenClientId, onAutoOpenComplete }) {
  const { clients, getPipelineSummary, loadClient, createNewClient } = useClients();
  const { currentUser, isSuperUser } = useUsers();
  const { config } = useConfig();
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const { byClient: unreadByClient } = useUnreadMessages(true);

  // Auto-open a client drawer when navigated from the Dashboard
  React.useEffect(() => {
    if (autoOpenClientId) {
      setSelectedClientId(autoOpenClientId);
      onAutoOpenComplete && onAutoOpenComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenClientId]);

  const baseSummary = getPipelineSummary();

  // Recompute the workforce KPIs LIVE from each client's employees, so the
  // header cards stay in sync with the Workforce tab analysis (instead of
  // relying on whatever stale `client.summary` was saved to the backend).
  const summary = useMemo(() => {
    let totalEmployees = 0;
    let eligibleEmployees = 0;
    let annualFicaSavings = 0;
    let annualHcRevenue = 0;
    let liveCount = 0;

    clients.forEach((client) => {
      const employees = Array.isArray(client.employees) ? client.employees : [];
      if (employees.length > 0 && config) {
        liveCount += 1;
        try {
          const analyzed = employees.map((e) => {
            const salaryNum = Number(e.salary) || Number(e.annualSalary) || 0;
            if (!salaryNum) return e;
            return optimizeEmployeeWithEligibility({ ...e, salary: salaryNum }, config);
          });
          totalEmployees += analyzed.length;
          analyzed.forEach((a) => {
            if (a.hasViable && a.optimal) {
              eligibleEmployees += 1;
              annualFicaSavings += a.optimal.employerAnnualFica || 0;
              annualHcRevenue += (a.optimal.spreadMonthly || 0) * 12;
            }
          });
        } catch (err) {
          // fall back to the saved summary for this client
          if (client.summary) {
            totalEmployees += client.summary.totalEmployees || 0;
            eligibleEmployees += client.summary.eligible || 0;
            annualFicaSavings += client.summary.annualEmployerFica || 0;
            annualHcRevenue += client.summary.annualHcRevenue || 0;
          }
        }
      } else if (client.summary) {
        // Fall back to saved summary when no live census is present
        totalEmployees += client.summary.totalEmployees || 0;
        eligibleEmployees += client.summary.eligible || 0;
        annualFicaSavings += client.summary.annualEmployerFica || 0;
        annualHcRevenue += client.summary.annualHcRevenue || 0;
      }
    });

    return {
      ...baseSummary,
      totalEmployees,
      eligibleEmployees,
      annualFicaSavings,
      annualHcRevenue,
    };
  }, [clients, config, baseSummary]);
  
  // Filter clients based on role (RBAC)
  const filteredClients = clients.filter(client => {
    // RBAC: Account Managers only see their assigned clients
    if (!isSuperUser(currentUser)) {
      if (client.crm.assignedTo !== currentUser.id) {
        return false;
      }
    }
    // Super Users see all clients (no assignedTo filter)
    
    const matchesSearch = !searchTerm || 
      client.company.legalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.company.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.contacts.some(c => 
        `${c.firstName} ${c.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
      );
    
    const matchesStage = stageFilter === 'all' || client.crm.stage === stageFilter;
    
    return matchesSearch && matchesStage;
  });
  
  const handleOpenClient = (clientId) => {
    loadClient(clientId);
    setSelectedClientId(clientId);
  };
  
  const handleCloseDrawer = () => {
    setSelectedClientId(null);
  };
  
  const getStageIcon = (stage) => {
    const stageInfo = PIPELINE_STAGES[stage] || PIPELINE_STAGES.lead;
    return <Icon name={stageInfo.icon} style={{ fontSize: 16, color: stageInfo.color }} />;
  };
  
  return (
    <div style={{ ...S.card, padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, margin: 0, marginBottom: 4 }}>
            Clients & Pipeline
          </h2>
          <p style={{ fontSize: 13, color: C.textLight, margin: 0 }}>
            Manage employer relationships from prospect to active plan
          </p>
        </div>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowAddClientModal(true);
          }}
          style={{
            padding: '10px 18px',
            background: C.green,
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            WebkitTapHighlightColor: 'rgba(0,0,0,0)',
            touchAction: 'manipulation',
          }}
        >
          <Icon name="add-circle-outline" style={{ fontSize: 16 }} />
          Add Client
        </button>
      </div>
      
      {/* Quick Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: 16, 
        marginBottom: 24 
      }}>
        <div style={{ ...S.card, padding: 16, background: C.bg }}>
          <div style={{ fontSize: 11, color: C.textLight, fontWeight: 600, marginBottom: 4 }}>
            TOTAL CLIENTS
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: C.text }}>
            {summary.totalClients}
          </div>
        </div>
        
        <div style={{ ...S.card, padding: 16, background: C.bg }}>
          <div style={{ fontSize: 11, color: C.textLight, fontWeight: 600, marginBottom: 4 }}>
            EMPLOYEES
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: C.text }}>
            {(summary.totalEmployees || 0).toLocaleString()}
          </div>
          <div style={{ fontSize: 10, color: C.textMid, marginTop: 2 }}>
            {(summary.eligibleEmployees || 0).toLocaleString()} eligible
          </div>
        </div>
        
        <div style={{ ...S.card, padding: 16, background: C.bg }}>
          <div style={{ fontSize: 11, color: C.textLight, fontWeight: 600, marginBottom: 4 }}>
            ANNUAL FICA SAVINGS
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: C.green }}>
            {fmt(summary.annualFicaSavings, 0)}
          </div>
        </div>
        
        <div style={{ ...S.card, padding: 16, background: C.bg }}>
          <div style={{ fontSize: 11, color: C.textLight, fontWeight: 600, marginBottom: 4 }}>
            ANNUAL HC REVENUE
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: C.blue }}>
            {fmt(summary.annualHcRevenue, 0)}
          </div>
        </div>
      </div>
      
      {/* Pipeline Funnel */}
      <div style={{ ...S.card, padding: 20, background: C.bg, marginBottom: 24 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0, marginBottom: 16, color: C.text }}>
          PIPELINE OVERVIEW
        </h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'space-between' }}>
          {['lead', 'prospect', 'proposal', 'negotiation', 'closed_won', 'active'].map((stage, index, arr) => {
            const stageInfo = PIPELINE_STAGES[stage];
            const count = summary.byStage[stage] || 0;
            const isLast = index === arr.length - 1;
            
            return (
              <React.Fragment key={stage}>
                <button
                  onClick={() => setStageFilter(stage)}
                  style={{
                    flex: 1,
                    minWidth: 100,
                    padding: '12px 16px',
                    background: stageFilter === stage ? stageInfo.color + '15' : 'white',
                    border: `2px solid ${stageFilter === stage ? stageInfo.color : C.border}`,
                    borderRadius: 8,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, justifyContent: 'center' }}>
                    <Icon name={stageInfo.icon} style={{ fontSize: 16, color: stageInfo.color }} />
                    <div style={{ fontSize: 11, fontWeight: 700, color: stageInfo.color }}>
                      {stageInfo.label.toUpperCase()}
                    </div>
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: C.text, textAlign: 'center' }}>
                    {count}
                  </div>
                </button>
                {!isLast && (
                  <div style={{ display: 'flex', alignItems: 'center', color: C.textLight }}>
                    <Icon name="arrow-forward-outline" style={{ fontSize: 20 }} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
        {stageFilter !== 'all' && (
          <button
            onClick={() => setStageFilter('all')}
            style={{
              marginTop: 12,
              padding: '6px 12px',
              background: 'transparent',
              border: `1px solid ${C.border}`,
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 11,
              fontWeight: 600,
              color: C.textMid,
            }}
          >
            <Icon name="close-circle-outline" style={{ fontSize: 12 }} /> Clear Filter
          </button>
        )}
      </div>
      
      {/* Follow-ups Due */}
      {(summary.followUpsDue.overdue.length > 0 || 
        summary.followUpsDue.today.length > 0 || 
        summary.followUpsDue.thisWeek.length > 0) && (
        <div style={{ ...S.card, padding: 20, background: C.bg, marginBottom: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0, marginBottom: 16, color: C.text }}>
            FOLLOW-UPS DUE
          </h3>
          
          <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontSize: 11, fontWeight: 600 }}>
            {summary.followUpsDue.overdue.length > 0 && (
              <div style={{ color: C.red }}>
                🔴 Overdue ({summary.followUpsDue.overdue.length})
              </div>
            )}
            {summary.followUpsDue.today.length > 0 && (
              <div style={{ color: C.orange }}>
                🟡 Due Today ({summary.followUpsDue.today.length})
              </div>
            )}
            {summary.followUpsDue.thisWeek.length > 0 && (
              <div style={{ color: C.green }}>
                🟢 This Week ({summary.followUpsDue.thisWeek.length})
              </div>
            )}
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[...summary.followUpsDue.overdue, ...summary.followUpsDue.today, ...summary.followUpsDue.thisWeek]
              .slice(0, 5)
              .map((followUp, index) => (
                <button
                  key={index}
                  onClick={() => handleOpenClient(followUp.clientId)}
                  style={{
                    padding: '10px 12px',
                    background: 'white',
                    border: `1px solid ${C.border}`,
                    borderRadius: 6,
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>
                      {followUp.clientName}
                    </div>
                    <div style={{ fontSize: 11, color: C.textMid, marginTop: 2 }}>
                      {followUp.action}
                    </div>
                  </div>
                  <div style={{ fontSize: 10, color: C.textLight }}>
                    {new Date(followUp.dueDate).toLocaleDateString()}
                  </div>
                </button>
              ))}
          </div>
        </div>
      )}
      
      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <div style={{ position: 'relative' }}>
            <Icon name="search-outline" style={{ 
              position: 'absolute', 
              left: 12, 
              top: '50%', 
              transform: 'translateY(-50%)', 
              fontSize: 16, 
              color: C.textLight 
            }} />
            <input
              id="clients-search"
              name="search"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search clients by name, city, or contact..."
              autoComplete="off"
              style={{
                ...S.input,
                paddingLeft: 40,
                fontSize: 13,
              }}
            />
          </div>
        </div>
        
        <StyledSelect
          value={stageFilter}
          onValueChange={setStageFilter}
          options={[
            { value: "all", label: `All Stages (${clients.length})` },
            ...Object.entries(PIPELINE_STAGES).map(([key, stage]) => ({
              value: key,
              label: `${stage.label} (${summary.byStage[key] || 0})`
            }))
          ]}
          style={{ width: 180, fontSize: 13 }}
        />
      </div>
      
      {/* Client List */}
      {filteredClients.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: 60, 
          color: C.textLight 
        }}>
          <Icon name="briefcase-outline" style={{ fontSize: 48, marginBottom: 12 }} />
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
            {searchTerm || stageFilter !== 'all' ? 'No clients match your filters' : 'No clients yet'}
          </div>
          <div style={{ fontSize: 12 }}>
            {searchTerm || stageFilter !== 'all' ? 'Try adjusting your search or filters' : 'Get started by uploading census data in the Workforce tab'}
          </div>
        </div>
      ) : (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: C.textMid }}>
                  STAGE
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: C.textMid }}>
                  COMPANY
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: C.textMid }}>
                  EMPLOYEES
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: C.textMid }}>
                  FICA/YR
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: C.textMid }}>
                  NEXT ACTION
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((client) => {
                const stageInfo = PIPELINE_STAGES[client.crm.stage] || PIPELINE_STAGES.lead;
                const eligible = client.summary?.eligible || 0;
                const total = client.summary?.totalEmployees || 0;
                const fica = client.summary?.annualEmployerFica || 0;
                
                return (
                  <tr
                    key={client.id}
                    onClick={() => handleOpenClient(client.id)}
                    style={{
                      borderBottom: `1px solid ${C.border}`,
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = C.bg}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {getStageIcon(client.crm.stage)}
                        <span style={{ fontSize: 12, fontWeight: 600, color: stageInfo.color }}>
                          {stageInfo.label}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                        {client.company.legalName || 'Unnamed Client'}
                        {unreadByClient[client.id] > 0 && (
                          <span
                            data-testid={`client-unread-badge-${client.id}`}
                            title={`${unreadByClient[client.id]} unread message${unreadByClient[client.id] !== 1 ? 's' : ''}`}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 3,
                              padding: '1px 7px',
                              borderRadius: 10,
                              background: '#dc2626',
                              color: '#fff',
                              fontSize: 10,
                              fontWeight: 800,
                            }}
                          >
                            <Icon name="chatbubble-ellipses" style={{ fontSize: 10 }} />
                            {unreadByClient[client.id]}
                          </span>
                        )}
                      </div>
                      {(client.company.city || client.company.state) && (
                        <div style={{ fontSize: 11, color: C.textLight }}>
                          {[client.company.city, client.company.state].filter(Boolean).join(', ')}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '16px' }}>
                      {total > 0 ? (
                        <>
                          <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                            {eligible}/{total}
                          </div>
                          <div style={{ fontSize: 11, color: C.textMid }}>
                            ({Math.round((eligible / total) * 100)}%)
                          </div>
                        </>
                      ) : (
                        <span style={{ fontSize: 12, color: C.textLight }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right' }}>
                      {fica > 0 ? (
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.green }}>
                          {fmt(fica, 0)}
                        </div>
                      ) : (
                        <span style={{ fontSize: 12, color: C.textLight }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '16px' }}>
                      {client.crm.followUp?.action ? (
                        <>
                          <div style={{ fontSize: 12, color: C.text }}>
                            {client.crm.followUp.action}
                          </div>
                          {client.crm.followUp.dueDate && (
                            <div style={{ fontSize: 10, color: C.textLight, marginTop: 2 }}>
                              Due: {new Date(client.crm.followUp.dueDate).toLocaleDateString()}
                            </div>
                          )}
                        </>
                      ) : (
                        <span style={{ fontSize: 12, color: C.textLight }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Client Detail Drawer */}
      {selectedClientId && (
        <ClientDetailDrawer
          clientId={selectedClientId}
          onClose={handleCloseDrawer}
          onOpenAnalysis={(clientId) => {
            // Ensure client stays loaded when switching tabs
            if (onOpenAnalysis) onOpenAnalysis(clientId);
          }}
        />
      )}
      
      {/* Add Client Modal */}
      {showAddClientModal && (
        <SaveEmployerDialog
          isOpen={showAddClientModal}
          onClose={() => setShowAddClientModal(false)}
        />
      )}
    </div>
  );
}
